/**
 * KAN-744: карточка менеджера: ФИО, телефон, заметки (Изменить для
 * владельца), часы для звонков (KAN-837), цифры по его клиентам, воронка менеджера (тот же блок, что на
 * дашборде, по /managers/<id>/funnel/) и список его клиентов.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Pencil, Phone } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FunnelStagesCard } from '@/components/dashboard/FunnelStagesCard';
import { ManagerDialog } from '@/components/managers/ManagerDialog';
import { ManagerSummaryCards } from '@/components/managers/ManagerSummaryCards';
import { ManagerClientsCard } from '@/components/managers/ManagerClientsCard';
import { ManagerAccountBlock } from '@/components/managers/ManagerAccountBlock';
import { ManagerSeniorToggle } from '@/components/managers/ManagerSeniorToggle';
import { ManagerTelegramBlock } from '@/components/managers/ManagerTelegramBlock';
import { ManagerAppBlock } from '@/components/managers/ManagerAppBlock';
import { ManagerWorkHoursBlock } from '@/components/managers/ManagerWorkHoursBlock';
import { ManagerCallLineSelect } from '@/components/managers/ManagerCallLineSelect';
import { useIsOwner } from '@/hooks/useCurrentUser';
import { managerFunnelKey, managerFunnelLoader, useManager } from '@/hooks/useManagers';
import { formatClientPhone } from '@/lib/clientListFormat';

const BREADCRUMBS_ROOT = { label: 'Менеджеры', href: '/managers' };

export function ManagerDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const isOwner = useIsOwner();
  const { data: manager, isLoading, isError } = useManager(id);
  const [editOpen, setEditOpen] = useState(false);
  const funnelLoad = useMemo(() => managerFunnelLoader(id), [id]);
  const funnelKey = useMemo(() => managerFunnelKey(id), [id]);
  const clientsParams = useMemo(() => ({ manager: id }), [id]);

  useEffect(() => {
    document.title = `${manager?.full_name ?? 'Менеджер'} - SMETA AI`;
  }, [manager?.full_name]);

  if (isLoading) {
    return (
      <PageLayout title="Менеджер" breadcrumbs={[BREADCRUMBS_ROOT]} backButton={{ href: '/managers' }}>
        <div className="flex justify-center py-12" data-testid="manager-loading">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </PageLayout>
    );
  }
  if (isError || !manager) {
    return (
      <PageLayout title="Менеджер" breadcrumbs={[BREADCRUMBS_ROOT]} backButton={{ href: '/managers' }}>
        <p className="py-8 text-center text-sm text-red-600" data-testid="manager-error">
          Менеджер не найден
        </p>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={manager.full_name}
      titleAddon={
        !manager.is_active || manager.is_senior ? (
          <span className="flex gap-2">
            {manager.is_senior && <Badge variant="secondary">Старший</Badge>}
            {!manager.is_active && <Badge variant="outline">В архиве</Badge>}
          </span>
        ) : undefined
      }
      breadcrumbs={[BREADCRUMBS_ROOT, { label: manager.full_name }]}
      backButton={{ href: '/managers' }}
      actions={
        isOwner ? (
          <Button variant="outline" data-testid="manager-edit-button" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Изменить
          </Button>
        ) : undefined
      }
      data-testid="manager-detail-page"
    >
      <div className="space-y-6">
        <div className="space-y-1 text-sm text-gray-700" data-testid="manager-header">
          {manager.phone && (
            <p className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-400" />
              {formatClientPhone(manager.phone)}
            </p>
          )}
          {manager.notes && <p className="whitespace-pre-line text-gray-600">{manager.notes}</p>}
        </div>
        {/* KAN-837: когда менеджеру можно звонить; правит владелец */}
        <ManagerWorkHoursBlock managerId={manager.id} workHours={manager.work_hours} canEdit={isOwner} />
        {/* KAN-847: линия входящих звонков; правит владелец */}
        <ManagerCallLineSelect managerId={manager.id} callLine={manager.call_line} canEdit={isOwner} />
        {/* KAN-828: признак старшего менеджера, владельцу и оператору */}
        <ManagerSeniorToggle managerId={manager.id} isSenior={!!manager.is_senior} />
        <ManagerSummaryCards summary={manager.summary} />
        <FunnelStagesCard
          title="Воронка менеджера"
          queryKey={funnelKey}
          load={funnelLoad}
          clientsParams={clientsParams}
          testId="manager-funnel-card"
        />
        <ManagerClientsCard managerId={manager.id} />
        {/* KAN-829: Telegram менеджера для уведомлений, владельцу и оператору */}
        <ManagerTelegramBlock
          managerId={manager.id}
          telegramLinked={!!manager.telegram_linked}
          telegramLinkedAt={manager.telegram_linked_at ?? null}
          isActive={manager.is_active}
        />
        {/* KAN-846: два способа дать доступ рядом: ссылка (KAN-834, владельцу и оператору)
            и логин с паролем (KAN-811, только владельцу) */}
        <div className="grid items-start gap-4 lg:grid-cols-2" data-testid="manager-access-options">
          <ManagerAppBlock managerId={manager.id} isActive={manager.is_active} />
          {isOwner && (
            <ManagerAccountBlock managerId={manager.id} account={manager.account} phone={manager.phone} />
          )}
        </div>
      </div>
      {isOwner && <ManagerDialog open={editOpen} onOpenChange={setEditOpen} manager={manager} />}
    </PageLayout>
  );
}
